import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useZohoFieldMappings } from '@/hooks/useZohoFieldMappings';
import { Plus, Edit2, Trash2, ArrowLeftRight, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

const FIELD_TYPES = [
  { value: 'text', label: 'Texto' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Telefone' },
  { value: 'select', label: 'Seleção' },
  { value: 'date', label: 'Data' },
  { value: 'number', label: 'Número' },
];

// Campos disponíveis no sistema
const SYSTEM_FIELDS = [
  { value: 'name', label: 'Nome Completo', type: 'text' },
  { value: 'first_name', label: 'Primeiro Nome', type: 'text' },
  { value: 'last_name', label: 'Sobrenome', type: 'text' },
  { value: 'email', label: 'Email', type: 'email' },
  { value: 'phone', label: 'Telefone', type: 'phone' },
  { value: 'status', label: 'Status', type: 'select' },
  { value: 'origin', label: 'Origem', type: 'text' },
  { value: 'market', label: 'Mercado', type: 'text' },
  { value: 'campaign_name', label: 'Nome da Campanha', type: 'text' },
  { value: 'ad_name', label: 'Nome do Anúncio', type: 'text' },
  { value: 'source', label: 'Fonte', type: 'text' },
];

export default function ZohoMapping() {
  const { mappings, loading, createMapping, updateMapping, deleteMapping, toggleActive } = useZohoFieldMappings();
  const [showDialog, setShowDialog] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [formData, setFormData] = useState({
    system_field: '',
    system_field_label: '',
    zoho_field: '',
    zoho_field_label: '',
    field_type: 'text',
    is_required: false,
    is_active: true,
  });

  const handleOpenDialog = (mapping = null) => {
    if (mapping) {
      setEditingMapping(mapping);
      setFormData({
        system_field: mapping.system_field,
        system_field_label: mapping.system_field_label,
        zoho_field: mapping.zoho_field,
        zoho_field_label: mapping.zoho_field_label,
        field_type: mapping.field_type,
        is_required: mapping.is_required,
        is_active: mapping.is_active,
      });
    } else {
      setEditingMapping(null);
      setFormData({
        system_field: '',
        system_field_label: '',
        zoho_field: '',
        zoho_field_label: '',
        field_type: 'text',
        is_required: false,
        is_active: true,
      });
    }
    setShowDialog(true);
  };

  const handleSystemFieldChange = (value) => {
    const field = SYSTEM_FIELDS.find(f => f.value === value);
    if (field) {
      setFormData({
        ...formData,
        system_field: field.value,
        system_field_label: field.label,
        field_type: field.type,
      });
    }
  };

  const handleSave = async () => {
    try {
      if (editingMapping) {
        await updateMapping(editingMapping.id, formData);
      } else {
        await createMapping(formData);
      }
      setShowDialog(false);
    } catch (error) {
      console.error('Error saving mapping:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja remover este mapeamento?')) {
      await deleteMapping(id);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">Carregando mapeamentos...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Mapeamento de Campos - Zoho CRM</h1>
          <p className="text-muted-foreground mt-2">
            Configure como os campos do sistema serão mapeados para o Zoho CRM
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="w-4 h-4 mr-2" />
          Novo Mapeamento
        </Button>
      </div>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <strong>Importante:</strong> Configure os mapeamentos antes de importar leads para o Zoho.
          Campos marcados como obrigatórios devem ter valores no sistema.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>Mapeamentos Configurados</CardTitle>
          <CardDescription>
            {mappings.length} campo{mappings.length !== 1 ? 's' : ''} mapeado{mappings.length !== 1 ? 's' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campo do Sistema</TableHead>
                <TableHead><ArrowLeftRight className="w-4 h-4 inline mr-2" />Mapeamento</TableHead>
                <TableHead>Campo no Zoho</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Obrigatório</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mappings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    Nenhum mapeamento configurado. Clique em "Novo Mapeamento" para começar.
                  </TableCell>
                </TableRow>
              ) : (
                mappings.map((mapping) => (
                  <TableRow key={mapping.id}>
                    <TableCell className="font-medium">
                      <div>
                        <div>{mapping.system_field_label}</div>
                        <div className="text-xs text-muted-foreground">{mapping.system_field}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <ArrowLeftRight className="w-4 h-4 text-muted-foreground" />
                    </TableCell>
                    <TableCell>
                      <div>
                        <div>{mapping.zoho_field_label}</div>
                        <div className="text-xs text-muted-foreground">{mapping.zoho_field}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{mapping.field_type}</Badge>
                    </TableCell>
                    <TableCell>
                      {mapping.is_required ? (
                        <Badge variant="destructive">Sim</Badge>
                      ) : (
                        <Badge variant="secondary">Não</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={mapping.is_active}
                        onCheckedChange={(checked) => toggleActive(mapping.id, checked)}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(mapping)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(mapping.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingMapping ? 'Editar Mapeamento' : 'Novo Mapeamento'}
            </DialogTitle>
            <DialogDescription>
              Configure como um campo do sistema será mapeado para o Zoho CRM
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Campo do Sistema</Label>
                {editingMapping ? (
                  <Input value={formData.system_field_label} disabled />
                ) : (
                  <Select
                    value={formData.system_field}
                    onValueChange={handleSystemFieldChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o campo" />
                    </SelectTrigger>
                    <SelectContent>
                      {SYSTEM_FIELDS.map((field) => (
                        <SelectItem key={field.value} value={field.value}>
                          {field.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="space-y-2">
                <Label>Campo no Zoho</Label>
                <Input
                  value={formData.zoho_field}
                  onChange={(e) => setFormData({ ...formData, zoho_field: e.target.value })}
                  placeholder="Ex: Last_Name"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Label do Campo do Sistema</Label>
                <Input
                  value={formData.system_field_label}
                  onChange={(e) => setFormData({ ...formData, system_field_label: e.target.value })}
                  placeholder="Ex: Nome Completo"
                />
              </div>

              <div className="space-y-2">
                <Label>Label do Campo no Zoho</Label>
                <Input
                  value={formData.zoho_field_label}
                  onChange={(e) => setFormData({ ...formData, zoho_field_label: e.target.value })}
                  placeholder="Ex: Last Name"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de Campo</Label>
                <Select
                  value={formData.field_type}
                  onValueChange={(value) => setFormData({ ...formData, field_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="required"
                    checked={formData.is_required}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_required: checked })}
                  />
                  <Label htmlFor="required">Campo obrigatório</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label htmlFor="active">Ativo</Label>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave}>
              {editingMapping ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
